import type { OrderDetails } from '@/entities/order/model'
import { useMemo } from 'react'
import { useStateConfigQuery, useZebraBarcodeEnabled } from '@/entities/state/model'
import { getGroupScanProgressCounts, isGroupAllCratesProcessed, isZebraScanProgressStatus } from './order.zebraScan'

export interface GroupScanState {
  /** Zebra scanning applies to this group: feature enabled and the group is inside the scan window. */
  showScanProgress: boolean
  /** Combined scanned barcodes across the group. */
  scanned: number
  /** Combined expected crates across the group. */
  total: number
  /** Every order in the group has all of its crates accounted for. */
  allProcessed: boolean
  /**
   * Whether the group may move on from Arrived (BD-8186). Only ever false while scanning
   * applies - a group outside the scan window, or with the feature off, is never gated.
   */
  canTransition: boolean
}

/**
 * Combined Zebra scan state for a group of orders.
 *
 * Single source for both the group card's action label and the transition gate, so the
 * button can never say the group is ready while the gate disagrees.
 */
export function useGroupScanState(orders: OrderDetails[]): GroupScanState {
  const zebraBarcodeEnabled = useZebraBarcodeEnabled()
  const { data: stateConfig } = useStateConfigQuery()
  const status = orders[0]?.status

  return useMemo(() => {
    const showScanProgress = zebraBarcodeEnabled
      && isZebraScanProgressStatus(status, stateConfig?.states)
    const { scanned, total } = getGroupScanProgressCounts(orders)
    const allProcessed = isGroupAllCratesProcessed(orders)

    return {
      showScanProgress,
      scanned,
      total,
      allProcessed,
      canTransition: !showScanProgress || allProcessed,
    }
  }, [orders, stateConfig?.states, status, zebraBarcodeEnabled])
}
